/**
 * LA CARTE « RÉTROACTION » DE L'ADMINISTRATEUR (chantier « Rétroaction », P1-J,
 * AB#876 — décision du fondateur du 15 octobre 2026).
 *
 * Ce qui dira si le retour SERT, semaine par semaine, sur les essais
 * d'exercice du manuel : l'élève réessaie-t-il après une erreur, y réussit-il,
 * et la même erreur revient-elle ? L'indicateur principal est la MÊME ERREUR
 * REVENUE SOUS 7 JOURS — plus bas, mieux c'est.
 *
 * Comptes ET taux : la console est pour le fondateur, jamais pour un élève.
 * Les définitions sont celles de l'API (`modules/retroaction/indicateurs.ts`) ;
 * cette carte ne recalcule rien, elle écrit les comptes et les taux reçus.
 * « — » : la fenêtre n'est pas encore écoulée, ou rien à compter.
 */
import type {
  CompteEtTauxDto,
  IndicateursRetroactionResponse,
  IndicateursRetroactionSemaineDto,
} from '@silex/shared';

import type { Resource } from '../lib/useAsync';

import { Chargement, ErreurRessource } from './EtatRessource';

/** « 43 % » ; « — » sans dénominateur. */
export function pourcent(c: CompteEtTauxDto): string {
  return c.taux === null ? '—' : `${Math.round(c.taux * 100)} %`;
}

/** « 3/7 · 43 % » ; « — » sans dénominateur. */
export function compteEtPourcent(c: CompteEtTauxDto): string {
  return c.taux === null ? '—' : `${c.n}/${c.sur} · ${pourcent(c)}`;
}

const SANS: CompteEtTauxDto = { n: 0, sur: 0, taux: null };

export function recurrenceA(s: IndicateursRetroactionSemaineDto, jours: number): CompteEtTauxDto {
  return s.recurrence.find((r) => r.fenetreJours === jours) ?? SANS;
}

/** « 12 oct. » — le lundi de la semaine. */
export function libelleSemaine(lundi: string): string {
  const d = new Date(`${lundi}T00:00:00Z`);
  return new Intl.DateTimeFormat('fr-CA', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(d);
}

/** La plus récente semaine dont la fenêtre de 7 jours est close pour au moins une erreur. */
export function derniereSemaineMesuree(
  semaines: readonly IndicateursRetroactionSemaineDto[],
): IndicateursRetroactionSemaineDto | undefined {
  return [...semaines].reverse().find((s) => recurrenceA(s, 7).taux !== null);
}

function EnTete({ data }: { readonly data: IndicateursRetroactionResponse }) {
  const s = derniereSemaineMesuree(data.semaines);
  if (s === undefined) {
    return (
      <p className="suivi-note" data-testid="retro-principal">
        Pas encore de mesure : aucune erreur dont la fenêtre de 7 jours soit écoulée.
      </p>
    );
  }
  const r7 = recurrenceA(s, 7);
  return (
    <p className="suivi-note" data-testid="retro-principal">
      Même erreur revenue sous 7 jours :{' '}
      <strong style={{ fontSize: 20, color: 'var(--ink)' }}>{pourcent(r7)}</strong> ({r7.n} sur{' '}
      {r7.sur}) — semaine du {libelleSemaine(s.semaine)} ; plus bas, mieux c’est.
    </p>
  );
}

function Tableau({ data }: { readonly data: IndicateursRetroactionResponse }) {
  /* La semaine la plus récente en haut. */
  const lignes = [...data.semaines].reverse();
  return (
    <div className="table-wrap" style={{ marginTop: 10 }}>
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">Semaine du</th>
            <th scope="col">Même erreur ≤ 7 j</th>
            <th scope="col">Élèves</th>
            <th scope="col">Essais</th>
            <th scope="col">En erreur</th>
            <th scope="col">Réessai &lt; 24 h</th>
            <th scope="col">Réussi au réessai</th>
            <th scope="col">Même erreur ≤ 21 j</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((s) => (
            <tr key={s.semaine}>
              <td>{libelleSemaine(s.semaine)}</td>
              <td>
                <strong>{compteEtPourcent(recurrenceA(s, 7))}</strong>
              </td>
              <td>{s.eleves}</td>
              <td>{s.essais}</td>
              <td>{s.essaisEnErreur}</td>
              <td>{compteEtPourcent(s.reessais)}</td>
              <td>{compteEtPourcent(s.reussitesAuReessai)}</td>
              <td>{compteEtPourcent(recurrenceA(s, 21))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CarteRetroaction({
  ressource,
}: {
  readonly ressource: Resource<IndicateursRetroactionResponse>;
}) {
  return (
    <section
      className="card"
      aria-label="Rétroaction"
      style={{ margin: 0, gridColumn: '1 / -1' }}
      data-testid="carte-retroaction"
    >
      <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>Rétroaction — 8 semaines</h2>
      {ressource.status === 'loading' ? <Chargement quoi="les indicateurs" /> : null}
      {ressource.status === 'error' ? <ErreurRessource message={ressource.message} /> : null}
      {ressource.status === 'ready' ? (
        <>
          <EnTete data={ressource.data} />
          <Tableau data={ressource.data} />
          <p className="suivi-note" style={{ marginTop: 8, fontSize: 12 }}>
            Essais d’exercice du manuel, comptés dans la semaine où ils sont faits. « — » : la
            fenêtre n’est pas encore écoulée, ou rien à compter. Ces taux ne sont jamais montrés à
            un élève.
          </p>
        </>
      ) : null}
    </section>
  );
}
